import { supabase } from '../lib/supabase'

/**
 * PORTAL STAFF AUTHENTICATION — Supabase Auth.
 *
 * Two gates, both required: a valid Supabase session, and a matching row in
 * staff_users with role 'owner' or 'staff'. An authenticated user who is not
 * Portal staff is signed straight back out.
 *
 * Only the publishable key is used; there is no service-role key here, and no
 * password is ever stored by the front end. Accounts are created by invitation
 * in Supabase, never from the public site.
 */

export type StaffRole = 'owner' | 'staff'

export interface StaffSession {
  name: string
  email: string
  role: StaffRole
}

export interface StaffAuthResult {
  ok: boolean
  error?: string
  session?: StaffSession
}

export interface StaffActionResult {
  ok: boolean
  error?: string
}

/** Deliberately generic so sign-in never reveals whether an account exists. */
export const GENERIC_SIGNIN_ERROR = 'Email or password incorrect'

const NOT_STAFF_ERROR = 'This account does not have Portal staff access.'
const UNAVAILABLE_ERROR = 'Staff sign-in is unavailable. Try again shortly.'

function nameFrom(email: string): string {
  const local = email.split('@')[0] ?? ''
  return (
    local
      .split(/[._-]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ') || 'Portal Staff'
  )
}

/**
 * Confirms the authenticated user is Portal staff. Returns null — after
 * signing the session out — if they are not.
 */
async function resolveStaff(userId: string, email: string): Promise<StaffSession | null> {
  if (!supabase) return null

  const { data, error } = await supabase
    .from('staff_users')
    .select('user_id, name, email, role')
    .eq('user_id', userId)
    .maybeSingle()

  if (error) console.warn(`[supabase] staff_users lookup failed: ${error.message}`)

  const role = data?.role as StaffRole | undefined
  if (!data || (role !== 'owner' && role !== 'staff')) {
    await supabase.auth.signOut()
    return null
  }

  return {
    name: (data.name as string | null) || nameFrom(email),
    email: (data.email as string | null) || email,
    role,
  }
}

/** Sign in with Supabase, then verify staff membership. */
export async function authenticateStaff(
  email: string,
  password: string,
): Promise<StaffAuthResult> {
  if (!supabase) return { ok: false, error: UNAVAILABLE_ERROR }

  const address = email.trim().toLowerCase()
  if (!address || !password) return { ok: false, error: GENERIC_SIGNIN_ERROR }

  // Visible in DevTools so a failed sign-in can be told apart from a client
  // that was never created: this line is always followed by an auth/v1/token
  // request to the Supabase project.
  console.info('[supabase] staff sign-in requested')

  const { data, error } = await supabase.auth.signInWithPassword({
    email: address,
    password,
  })

  if (error) console.warn(`[supabase] staff sign-in rejected: ${error.message}`)

  // Every failure reads the same, so the screen never reveals what exists.
  if (error || !data.user) return { ok: false, error: GENERIC_SIGNIN_ERROR }

  const session = await resolveStaff(data.user.id, data.user.email ?? address)
  if (!session) return { ok: false, error: NOT_STAFF_ERROR }

  return { ok: true, session }
}

/** Restores a staff session on load — Supabase persists it across refreshes. */
export async function loadStaffSession(): Promise<StaffSession | null> {
  if (!supabase) return null

  const { data } = await supabase.auth.getSession()
  const user = data.session?.user
  if (!user) return null

  return resolveStaff(user.id, user.email ?? '')
}

/** Ends the Supabase session entirely. */
export async function clearStaffSession(): Promise<void> {
  if (!supabase) return
  await supabase.auth.signOut()
}

/** Supabase password reset — a real email is sent by Supabase. */
export async function requestStaffPasswordReset(email: string): Promise<StaffActionResult> {
  if (!supabase) return { ok: false, error: UNAVAILABLE_ERROR }

  const address = email.trim().toLowerCase()
  if (!address) return { ok: false, error: 'Enter your Portal email address.' }

  /* No redirect: recovery is completed by entering the emailed code, so the
     link is never consumed by a mail scanner before the staff member arrives. */
  const { error } = await supabase.auth.resetPasswordForEmail(address)

  // Never confirm whether the address is registered.
  if (error) console.warn(`[supabase] password reset failed: ${error.message}`)
  return { ok: true }
}

const CODE_ERROR = 'That recovery code is no longer valid. Please request a new one.'

/** Exchanges the emailed recovery code for a short-lived recovery session. */
export async function verifyStaffRecoveryCode(
  email: string,
  token: string,
): Promise<StaffActionResult> {
  if (!supabase) return { ok: false, error: UNAVAILABLE_ERROR }

  const address = email.trim().toLowerCase()
  /* Spaces removed, otherwise passed exactly as entered — the length is
     Supabase's to decide, not this screen's. */
  const code = token.replace(/\s+/g, '')
  if (!address || !code) return { ok: false, error: CODE_ERROR }

  const { data, error } = await supabase.auth.verifyOtp({
    email: address,
    token: code,
    type: 'recovery',
  })

  if (error || !data.session) {
    if (error) console.warn(`[supabase] recovery code rejected: ${error.message}`)
    return { ok: false, error: CODE_ERROR }
  }
  return { ok: true }
}

/**
 * Sets a new password from a recovery link. The link itself is the proof of
 * identity, so no current password is asked for — but the recovery session is
 * ended immediately afterwards, so a reset never becomes a way in.
 */
export async function completeStaffPasswordReset(
  password: string,
  confirm: string,
): Promise<StaffActionResult> {
  if (!supabase) return { ok: false, error: UNAVAILABLE_ERROR }

  if (password.length < 8) return { ok: false, error: 'Choose a password of at least 8 characters.' }
  if (password !== confirm) return { ok: false, error: 'Passwords do not match.' }

  const { error } = await supabase.auth.updateUser({ password })
  if (error) {
    console.warn(`[supabase] password update failed: ${error.message}`)
    return { ok: false, error: error.message }
  }

  await supabase.auth.signOut()
  return { ok: true }
}

/** Sets a new password for the signed-in staff member. */
export async function changeStaffPassword(
  email: string,
  current: string,
  password: string,
  confirm: string,
): Promise<StaffActionResult> {
  if (!supabase) return { ok: false, error: UNAVAILABLE_ERROR }

  if (password.length < 8) return { ok: false, error: 'Choose a password of at least 8 characters.' }
  if (password !== confirm) return { ok: false, error: 'Passwords do not match.' }

  // Re-authenticate so a left-open session cannot change the password.
  const { error: reauth } = await supabase.auth.signInWithPassword({ email, password: current })
  if (reauth) return { ok: false, error: 'Current password incorrect.' }

  const { error } = await supabase.auth.updateUser({ password })
  if (error) return { ok: false, error: error.message }

  return { ok: true }
}
